// src/features/tower/components/FootageTab.tsx

import React, { useEffect, useState } from "react";
import { FiSearch } from "react-icons/fi";

const PREPARING_MESSAGES = [
  "Preparing footage for playback…",
  "Converting to a browser-friendly format…",
  "First view of a clip takes a moment…",
  "Almost there — fetching the stream…",
  "Hang tight, this only happens the first time…",
];

type WindowT = {
  windowId: string;
  cameraId: string;
  cameraLabel: string;
  startTime: string;
  endTime: string;
  status: string;
  available: boolean;
  durationSeconds: number;
  playbackPath: string;
};

function clockOf(iso: string): string {
  const t = String(iso || "").split("T")[1] || "";
  const hhmm = t.slice(0, 5);
  if (!hhmm) return String(iso || "");
  const [hStr, mStr] = hhmm.split(":");
  let h = Number(hStr);
  let m = Number(mStr);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm;
  if (m >= 60) {
    h = (h + Math.floor(m / 60)) % 24;
    m = m % 60;
  }
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function todaySAST(): string {
  const now = new Date(
    new Date().toLocaleString("en-US", { timeZone: "Africa/Johannesburg" })
  );
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function looksLikeKey(v: string): boolean {
  return /^[A-Z0-9]{4}-[A-Z0-9]{4}$/i.test(String(v || "").trim());
}

function looksLikeEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v || "").trim());
}

// Group windows by camera, preserving time order within each group.
function groupByCamera(windows: WindowT[]): { label: string; windows: WindowT[] }[] {
  const map: Record<string, { label: string; windows: WindowT[] }> = {};
  const order: string[] = [];
  for (const w of windows) {
    const key = w.cameraId;
    if (!map[key]) {
      map[key] = { label: w.cameraLabel, windows: [] };
      order.push(key);
    }
    map[key].windows.push(w);
  }
  return order.map((k) => map[k]);
}

export function FootageTab() {
  const [query, setQuery] = useState<string>("");
  const [orderInfo, setOrderInfo] = useState<any>(null);
  const [keyInfo, setKeyInfo] = useState<any>(null);
  const [windows, setWindows] = useState<WindowT[]>([]);
  const [gridUrls, setGridUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [note, setNote] = useState<string>("");

  const [showGenerate, setShowGenerate] = useState(false);
  const [genDate, setGenDate] = useState<string>(todaySAST());
  const [genEmail, setGenEmail] = useState<string>("");
  const [genSite, setGenSite] = useState<string>("gardens");
  const [genBusy, setGenBusy] = useState(false);
  const [genResult, setGenResult] = useState<string>("");
  const [genOk, setGenOk] = useState(false);

  const [playing, setPlaying] = useState<{
    title: string;
    url: string;
    gcsPath: string;
  } | null>(null);
  const [playLoading, setPlayLoading] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [prepareTries, setPrepareTries] = useState(0);
  const [msgIndex, setMsgIndex] = useState(0);

  useEffect(() => {
    const hash = window.location.hash;
    const match = hash.match(/[?&]order=([^&]+)/);
    if (match) {
      const id = decodeURIComponent(match[1]);
      setQuery(id);
      setTimeout(() => {
        runLookup(id);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!preparing) {
      setMsgIndex(0);
      return;
    }
    const iv = setInterval(() => {
      setMsgIndex((i) => (i + 1) % PREPARING_MESSAGES.length);
    }, 3000);
    return () => clearInterval(iv);
  }, [preparing]);

  const resetResults = () => {
    setWindows([]);
    setGridUrls({});
    setNote("");
    setOrderInfo(null);
    setKeyInfo(null);
  };

  const triggerAndPollGrid = (gcsPath: string, tryNum: number = 0) => {
    if (tryNum === 0) {
      fetch(
        "/.netlify/functions/get-footage?mode=prepare-report&gcsPath=" +
          encodeURIComponent(gcsPath)
      ).catch(() => {});
      fetch(
        "/.netlify/functions/get-footage?mode=grid&gcsPath=" +
          encodeURIComponent(gcsPath)
      )
        .then((r) => r.json())
        .then((json) => {
          if (json.url) {
            setGridUrls((prev) => ({ ...prev, [gcsPath]: json.url }));
            return;
          }
          triggerAndPollGrid(gcsPath, 1);
        })
        .catch(() => triggerAndPollGrid(gcsPath, 1));
      return;
    }
    if (tryNum >= 20) return;
    setTimeout(() => {
      fetch(
        "/.netlify/functions/get-footage?mode=grid&gcsPath=" +
          encodeURIComponent(gcsPath)
      )
        .then((r) => r.json())
        .then((json) => {
          if (json.url) {
            setGridUrls((prev) => ({ ...prev, [gcsPath]: json.url }));
          } else {
            triggerAndPollGrid(gcsPath, tryNum + 1);
          }
        })
        .catch(() => triggerAndPollGrid(gcsPath, tryNum + 1));
    }, 15000);
  };

  const startGridPolling = (w: WindowT[]) => {
    for (const win of w) {
      if (win.available && win.playbackPath) {
        triggerAndPollGrid(win.playbackPath);
      }
    }
  };

  const runLookup = async (raw: string) => {
    const value = String(raw || "").trim();
    if (!value) return;

    setLoading(true);
    setError("");
    resetResults();

    try {
      const keyFirst = looksLikeKey(value);
      let handled = keyFirst
        ? await tryKey(value)
        : await tryOrder(value);

      if (!handled) {
        handled = keyFirst ? await tryOrder(value) : await tryKey(value);
      }

      if (!handled) {
        setNote(
          `Nothing matched "${value}". Check the order number or footage key ` +
            `and try again — keys look like 3VEG-B55X, order numbers like 880C4.`
        );
      }
    } catch (e: any) {
      setError(e?.message || "Could not look up that reference.");
    } finally {
      setLoading(false);
    }
  };

  const tryOrder = async (id: string): Promise<boolean> => {
    const res = await fetch(
      "/.netlify/functions/get-footage?mode=order&orderId=" +
        encodeURIComponent(id)
    );
    const json = await res.json();
    if (!res.ok) throw new Error(json.message || "Lookup failed.");

    if (!json.found) return false;

    setOrderInfo(json);

    if (!json.hasFootage) {
      setNote(json.message || "Footage is only available for Gardens orders.");
      return true;
    }

    const w: WindowT[] = Array.isArray(json.windows) ? json.windows : [];
    setWindows(w);
    if (w.length === 0) {
      setNote(
        `Order ${id} found (${json.window?.start}–${json.window?.end}) ` +
          `but no windows are indexed yet. ` +
          `Footage may still be uploading — check back in 15-30 minutes.`
      );
    } else {
      startGridPolling(w);
    }
    return true;
  };

  const tryKey = async (code: string): Promise<boolean> => {
    const res = await fetch(
      "/.netlify/functions/get-footage?mode=key&key=" +
        encodeURIComponent(code)
    );
    const json = await res.json();
    if (!res.ok) throw new Error(json.message || "Lookup failed.");

    if (!json.found) return false;

    if (!json.valid) {
      setNote(json.message || "That key is no longer active.");
      return true;
    }

    setKeyInfo(json);
    const w: WindowT[] = Array.isArray(json.windows) ? json.windows : [];
    setWindows(w);
    if (w.length === 0) {
      setNote(
        `Key ${json.key} covers ${json.window?.start}–${json.window?.end} on ` +
          `${json.businessDate}, but no windows are indexed for that period.`
      );
    } else {
      startGridPolling(w);
    }
    return true;
  };

  const runSearch = () => runLookup(query);

  const runGenerate = async () => {
    const email = genEmail.trim();

    if (!looksLikeEmail(email)) {
      setGenOk(false);
      setGenResult(
        "That doesn't look like an email address. Check the spelling and try again."
      );
      return;
    }

    if (!genDate) return;

    setGenBusy(true);
    setGenResult("");
    setGenOk(false);
    try {
      const res = await fetch("/.netlify/functions/generate-footage-key", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: genDate, email, site: genSite }),
      });
      const json = await res.json();

      if (json.created && json.emailed) {
        setGenOk(true);
        setGenResult(
          `Key sent to ${json.sentTo}. It covers ${json.windowStart}–${json.windowEnd} on ${json.date}.`
        );
      } else if (json.created && !json.emailed) {
        setGenOk(false);
        setGenResult(
          json.message ||
            `Key ${json.key} was created but the email didn't send. Check the address and try again.`
        );
      } else {
        setGenResult(json.message || json.error || "No key could be created.");
      }
    } catch (e: any) {
      setGenResult(e?.message || "Could not generate a key.");
    } finally {
      setGenBusy(false);
    }
  };

  useEffect(() => {
    const available = windows.filter((w) => w.available && w.playbackPath);
    if (available.length === 0) return;
    for (const w of available) {
      fetch(
        "/.netlify/functions/get-footage?mode=prepare&gcsPath=" +
          encodeURIComponent(w.playbackPath)
      ).catch(() => {});
    }
  }, [windows]);

  const playWindow = async (w: WindowT) => {
    if (!w.available || !w.playbackPath) return;
    openPlayer(
      `${w.cameraLabel} · ${clockOf(w.startTime)}-${clockOf(w.endTime)}`,
      w.playbackPath
    );
  };

  const openPlayer = async (title: string, gcsPath: string) => {
    setPlayLoading(true);
    setPreparing(false);
    setPrepareTries(0);
    setPlaying({ title, url: "", gcsPath });
    try {
      const res = await fetch(
        "/.netlify/functions/get-footage?mode=playable&gcsPath=" +
          encodeURIComponent(gcsPath)
      );
      const json = await res.json();
      if (!res.ok || !json.url) {
        setPreparing(true);
        setPlaying({ title, url: "", gcsPath });
        setPlayLoading(false);
        scheduleRetry(title, gcsPath, 0);
        return;
      }
      setPlaying({ title, url: json.url, gcsPath });
    } catch (e: any) {
      setPreparing(true);
      setPlaying({ title, url: "", gcsPath });
      scheduleRetry(title, gcsPath, 0);
    } finally {
      setPlayLoading(false);
    }
  };

  const scheduleRetry = (title: string, gcsPath: string, tryNum: number) => {
    if (tryNum >= 18) {
      setPreparing(false);
      setError(
        "This clip is taking longer than usual to prepare. Give it a minute and open it again."
      );
      setPlaying(null);
      return;
    }
    setPrepareTries(tryNum + 1);
    setTimeout(async () => {
      try {
        const res = await fetch(
          "/.netlify/functions/get-footage?mode=playable&gcsPath=" +
            encodeURIComponent(gcsPath)
        );
        const json = await res.json();
        if (json.url) {
          setPlaying({ title, gcsPath, url: json.url });
        }
      } catch {
        /* onVideoError / next retry will handle it */
      }
    }, 15000);
  };

  const onVideoError = () => {
    if (!playing) return;
    setPreparing(true);
    scheduleRetry(playing.title, playing.gcsPath, prepareTries);
  };

  const readyWindows = windows.filter((w) => w.available && w.playbackPath);
  const cameraGroups = groupByCamera(readyWindows);

  return (
    <div style={{ padding: "20px 0 40px" }}>
      <style>{`
        .ft-input {
          border: 1px solid var(--ct-line);
          background: var(--ct-surface);
          color: var(--ct-ink);
          border-radius: 8px;
          padding: 9px 13px;
          font-family: inherit;
          font-size: 13.5px;
          outline: none;
        }
        .ft-input::placeholder { color: var(--ct-ink-3); }
        .ft-input:focus {
          border-color: var(--ct-accent);
          box-shadow: 0 0 0 3px var(--ct-accent-bg);
        }

        .ft-btn {
          border-radius: 8px; padding: 9px 15px; cursor: pointer;
          font-family: inherit; font-size: 13.5px; font-weight: 600;
          border: 1px solid var(--ct-line);
          background: var(--ct-surface);
          color: var(--ct-ink);
          transition: background .12s ease, border-color .12s ease, color .12s ease;
        }
        .ft-btn:hover:not(:disabled) {
          border-color: var(--ct-accent);
          color: var(--ct-accent);
        }

        .ft-btn-primary {
          background: var(--ct-accent);
          border-color: var(--ct-accent);
          color: #ffffff;
        }
        .ft-btn-primary:hover:not(:disabled) { filter: brightness(1.08); color: #fff; }

        .ft-btn-primary:disabled {
          background: var(--ct-accent-bg);
          border-color: var(--ct-accent);
          color: var(--ct-accent);
          cursor: not-allowed;
        }

        .ft-tile { transition: border-color .12s ease, box-shadow .12s ease; }
        .ft-tile:hover {
          border-color: var(--ct-ink-3) !important;
          box-shadow: 0 2px 8px rgba(15,23,42,0.06);
        }

        .ft-watch {
          border-radius: 7px; padding: 7px 10px; cursor: pointer;
          font-family: inherit; font-size: 12.5px; font-weight: 600;
          border: 1px solid var(--ct-line);
          background: var(--ct-surface);
          color: var(--ct-ink-2);
          transition: all .12s ease;
          margin-top: 10px; width: 100%;
        }
        .ft-watch:hover {
          border-color: var(--ct-accent);
          color: var(--ct-accent);
          background: var(--ct-accent-bg);
        }

        @keyframes ft-spin { to { transform: rotate(360deg); } }
        .ft-spinner {
          width: 34px; height: 34px; border-radius: 50%;
          border: 2px solid var(--ct-line);
          border-top-color: var(--ct-accent);
          animation: ft-spin 0.8s linear infinite;
          margin: 0 auto;
        }
        @keyframes ft-fade { from { opacity: 0.35; } to { opacity: 1; } }
      `}</style>

      {/* LOOKUP */}
      <div
        style={{
          border: "1px solid var(--ct-line)",
          background: "var(--ct-surface)",
          borderRadius: 10,
          padding: "18px 20px",
          marginBottom: 20,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div style={{ position: "relative", flex: "1 1 320px", maxWidth: 520 }}>
            <FiSearch
              size={15}
              style={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--ct-ink-3)",
                pointerEvents: "none",
              }}
            />
            <input
              className="ft-input"
              placeholder="Order number or footage key"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") runSearch();
              }}
              style={{ width: "100%", paddingLeft: 34 }}
            />
          </div>

          <button
            className="ft-btn ft-btn-primary"
            onClick={runSearch}
            disabled={!query.trim()}
          >
            Find footage
          </button>

          <div style={{ flex: 1 }} />

          <button
            className="ft-btn"
            onClick={() => setShowGenerate((s) => !s)}
          >
            {showGenerate ? "Close" : "Generate key"}
          </button>
        </div>

        <div
          style={{
            color: "var(--ct-ink-3)",
            fontSize: 12.5,
            marginTop: 10,
          }}
        >
          Enter an order number such as 880C4, or an opening-period key such as
          3VEG-B55X.
        </div>

        {/* GENERATE panel */}
        {showGenerate && (
          <div
            style={{
              marginTop: 16,
              paddingTop: 16,
              borderTop: "1px solid var(--ct-line)",
            }}
          >
            <div
              style={{
                color: "var(--ct-ink-2)",
                fontSize: 13.5,
                marginBottom: 12,
                lineHeight: 1.5,
                maxWidth: 560,
              }}
            >
              A key covers the opening period at the chosen branch — up to the
              first order of that day.
            </div>

            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <select
                className="ft-input"
                value={genSite}
                onChange={(e) => setGenSite(e.target.value)}
                style={{ cursor: "pointer" }}
              >
                <option value="gardens">Gardens</option>
                <option value="constantia">Constantia</option>
                <option value="tableview">Tableview</option>
              </select>

              <label
                style={{
                  color: "var(--ct-ink-2)",
                  fontSize: 13.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                Date
                <input
                  type="date"
                  className="ft-input"
                  value={genDate}
                  onChange={(e) => setGenDate(e.target.value)}
                />
              </label>

              <input
                className="ft-input"
                type="email"
                placeholder="Send to (email address)"
                value={genEmail}
                onChange={(e) => setGenEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") runGenerate();
                }}
                style={{ width: 280 }}
              />

              <button
                className="ft-btn ft-btn-primary"
                onClick={runGenerate}
                disabled={genBusy || !genDate || !genEmail.trim()}
              >
                {genBusy ? "Sending…" : "Send key"}
              </button>
            </div>

            {genResult && (
              <div
                style={{
                  marginTop: 12,
                  color: genOk ? "var(--ct-ok)" : "var(--ct-crit)",
                  fontSize: 13.5,
                  lineHeight: 1.5,
                }}
              >
                {genResult}
              </div>
            )}
          </div>
        )}

        {/* order context line */}
        {orderInfo && orderInfo.found && (
          <div
            style={{
              marginTop: 16,
              paddingTop: 14,
              borderTop: "1px solid var(--ct-line)",
              color: "var(--ct-ink-2)",
              fontSize: 13.5,
              lineHeight: 1.6,
            }}
          >
            <span style={{ color: "var(--ct-ink)", fontWeight: 600 }}>
              {orderInfo.customer}
            </span>
            {orderInfo.brand ? ` · ${orderInfo.brand}` : ""} ·{" "}
            {orderInfo.location} · {orderInfo.businessDate}
            {orderInfo.window && (
              <>
                {" "}
                ·{" "}
                <span className="ct-num" style={{ color: "var(--ct-ink)" }}>
                  {orderInfo.window.start}–{orderInfo.window.end}
                </span>
              </>
            )}
          </div>
        )}

        {/* key context line */}
        {keyInfo && (
          <div
            style={{
              marginTop: 16,
              paddingTop: 14,
              borderTop: "1px solid var(--ct-line)",
              color: "var(--ct-ink-2)",
              fontSize: 13.5,
              lineHeight: 1.6,
            }}
          >
            <span style={{ color: "var(--ct-ink)", fontWeight: 600 }}>
              Opening period
            </span>{" "}
            · {keyInfo.site ? keyInfo.site.charAt(0).toUpperCase() + keyInfo.site.slice(1) : "Gardens"} · {keyInfo.businessDate}
            {keyInfo.window && (
              <>
                {" "}
                ·{" "}
                <span className="ct-num" style={{ color: "var(--ct-ink)" }}>
                  {keyInfo.window.start}–{keyInfo.window.end}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      {/* states */}
      {loading && (
        <div
          style={{
            color: "var(--ct-ink-3)",
            padding: "48px 0",
            textAlign: "center",
            fontSize: 13.5,
          }}
        >
          Loading footage…
        </div>
      )}

      {error && (
        <div
          style={{
            border: "1px solid var(--ct-line)",
            background: "var(--ct-surface)",
            borderRadius: 10,
            padding: "14px 16px",
            color: "var(--ct-ink-2)",
            margin: "10px 0",
            fontSize: 13.5,
          }}
        >
          Couldn't load that footage. Try again in a moment.
        </div>
      )}

      {note && !loading && (
        <div
          style={{
            color: "var(--ct-ink-2)",
            fontSize: 13.5,
            lineHeight: 1.6,
            padding: "8px 0 20px",
            maxWidth: 640,
          }}
        >
          {note}
        </div>
      )}

      {/* FRAME GRIDS — grouped by camera */}
      {!loading && cameraGroups.length > 0 && cameraGroups.map((group) => (
        <div key={group.label} style={{ marginBottom: 24 }}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              marginBottom: 14,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: "var(--ct-ink)",
                  letterSpacing: "-0.01em",
                }}
              >
                {keyInfo ? "Opening period" : "Order cycle"}
              </div>
              <div
                style={{
                  color: "var(--ct-ink-3)",
                  fontSize: 12.5,
                  marginTop: 2,
                }}
              >
                {group.label} · one frame every 5 seconds
              </div>
            </div>
            <div style={{ color: "var(--ct-ink-3)", fontSize: 12.5 }}>
              {group.windows.length} window
              {group.windows.length === 1 ? "" : "s"}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(224px, 1fr))",
              gap: 12,
            }}
          >
            {group.windows.map((w) => {
              const gridUrl = gridUrls[w.playbackPath];
              const ready = !!gridUrl;
              return (
                <div
                  key={w.windowId}
                  className="ft-tile"
                  style={{
                    textAlign: "left",
                    border: "1px solid var(--ct-line)",
                    background: "var(--ct-surface)",
                    borderRadius: 10,
                    padding: "14px 15px",
                    fontFamily: "inherit",
                  }}
                >
                  <button
                    onClick={() =>
                      ready ? window.open(gridUrl, "_blank") : null
                    }
                    disabled={!ready}
                    style={{
                      all: "unset",
                      cursor: ready ? "pointer" : "default",
                      display: "block",
                      width: "100%",
                    }}
                  >
                    <div
                      className="ct-num"
                      style={{
                        color: "var(--ct-ink)",
                        fontWeight: 600,
                        fontSize: 15,
                        marginBottom: 3,
                      }}
                    >
                      {clockOf(w.startTime)}–{clockOf(w.endTime)}
                    </div>
                    <div
                      style={{
                        color: ready ? "var(--ct-accent)" : "var(--ct-ink-3)",
                        fontSize: 12.5,
                        fontWeight: ready ? 600 : 400,
                      }}
                    >
                      {ready ? "Open frame grid" : "Building frame grid…"}
                    </div>
                  </button>
                  <button
                    className="ft-watch"
                    onClick={() => playWindow(w)}
                    title="Watch the 5-minute video for this window"
                  >
                    Watch video
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* player modal */}
      {playing && (
        <div
          onClick={() => setPlaying(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.55)",
            zIndex: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 820,
              border: "1px solid var(--ct-line)",
              borderRadius: 12,
              background: "var(--ct-surface)",
              padding: 18,
              boxShadow: "0 20px 60px rgba(15,23,42,0.25)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 14,
              }}
            >
              <div
                style={{
                  color: "var(--ct-ink)",
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                {playing.title}
              </div>
              <button
                onClick={() => setPlaying(null)}
                className="ft-btn"
                style={{ padding: "6px 12px", fontSize: 12.5 }}
              >
                Close
              </button>
            </div>
            {playLoading || !playing.url ? (
              <div style={{ textAlign: "center", padding: "56px 0" }}>
                <div className="ft-spinner" />
                <div
                  style={{
                    color: "var(--ct-ink-3)",
                    fontSize: 13.5,
                    marginTop: 16,
                  }}
                >
                  Loading footage…
                </div>
              </div>
            ) : (
              <>
                <video
                  key={playing.url}
                  src={playing.url}
                  controls
                  autoPlay
                  onError={onVideoError}
                  onLoadedData={() => setPreparing(false)}
                  style={{
                    width: "100%",
                    borderRadius: 8,
                    background: "#000",
                    display: preparing ? "none" : "block",
                  }}
                />
                {preparing && (
                  <div style={{ textAlign: "center", padding: "48px 20px" }}>
                    <div className="ft-spinner" />
                    <div
                      key={msgIndex}
                      style={{
                        color: "var(--ct-ink-2)",
                        fontSize: 13.5,
                        marginTop: 18,
                        animation: "ft-fade 0.4s ease",
                      }}
                    >
                      {PREPARING_MESSAGES[msgIndex]}
                    </div>
                    <div
                      style={{
                        color: "var(--ct-ink-3)",
                        fontSize: 12.5,
                        marginTop: 8,
                      }}
                    >
                      This only happens the first time a clip is opened.
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
